import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Alert,
  LinearProgress,
  useTheme,
  alpha,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Badge,
  Avatar,
  InputAdornment,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from "@mui/material";
import {
  CalendarToday,
  CalendarViewWeek,
  CalendarViewMonth,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  LocalHospital as DoctorIcon,
  AccessTime as TimeIcon,
  Notes as NotesIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Today as TodayIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Event as EventIcon,
} from "@mui/icons-material";

import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs"; // ✅ Import Dayjs type
import "dayjs/locale/ro";
import axios from "axios";

// ✅ Keep date-fns for your existing date operations
import {
  format,
  formatISO,
  subDays,
  addDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  parseISO,
} from "date-fns";
import { ro } from "date-fns/locale";

// Enhanced Appointment Interface
interface Appointment {
  id: number;
  PatientId: number;
  DoctorId: number;
  PatientName?: string;
  DoctorName?: string;
  date: string;
  time: string;
  reason: string;
  status: "scheduled" | "completed" | "canceled" | "confirmed";
  notes?: string;
  duration?: number; // in minutes
  priority?: "low" | "medium" | "high" | "urgent";
  createdAt: string;
  updatedAt: string;
}

// Supporting Interfaces
interface Doctor {
  id: number;
  name: string;
  specialization?: string;
  email?: string;
  phone?: string;
}

interface Patient {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface AppointmentFilters {
  doctorId?: number;
  patientId?: number;
  status?: string;
  priority?: string;
  dateFrom?: Date;
  dateTo?: Date;
  searchTerm?: string;
}

type ViewMode = "day" | "week" | "month" | "list";

interface AppointmentScheduleProps {
  title?: string;
  height?: number;
  defaultView?: ViewMode;
}

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
const API_ENDPOINTS = {
  appointments: "/appointments",
  createAppointment: "/appointments",
  updateAppointment: "/appointments",
  deleteAppointment: "/appointments",
  doctors: "/doctors",
  patients: "/patients",
};

// Mock Data Generators
const generateMockDoctors = (): Doctor[] => [
  {
    id: 1,
    name: "Dr. Maria Popescu",
    specialization: "Cardiologie",
    email: "maria.popescu@clinic.ro",
    phone: "0721234567",
  },
  {
    id: 2,
    name: "Dr. Ion Ionescu",
    specialization: "Neurologie",
    email: "ion.ionescu@clinic.ro",
    phone: "0721234568",
  },
  {
    id: 3,
    name: "Dr. Ana Vasilescu",
    specialization: "Pediatrie",
    email: "ana.vasilescu@clinic.ro",
    phone: "0721234569",
  },
  {
    id: 4,
    name: "Dr. Gheorghe Marinescu",
    specialization: "Ortopedie",
    email: "gheorghe.marinescu@clinic.ro",
    phone: "0721234570",
  },
];

const generateMockPatients = (): Patient[] => [
  {
    id: 1,
    name: "Elena Gheorghiu",
    email: "elena.gheorghiu@email.ro",
    phone: "0731234567",
  },
  {
    id: 2,
    name: "Mihai Constantinescu",
    email: "mihai.constantinescu@email.ro",
    phone: "0731234568",
  },
  {
    id: 3,
    name: "Diana Popescu",
    email: "diana.popescu@email.ro",
    phone: "0731234569",
  },
  {
    id: 4,
    name: "Alexandru Radu",
    email: "alex.radu@email.ro",
    phone: "0731234570",
  },
];

const generateMockAppointments = (): Appointment[] => {
  const doctors = generateMockDoctors();
  const patients = generateMockPatients();
  const now = new Date();

  return [
    {
      id: 1,
      PatientId: 1,
      DoctorId: 1,
      PatientName: "Elena Gheorghiu",
      DoctorName: "Dr. Maria Popescu",
      date: format(now, "yyyy-MM-dd"),
      time: "09:00",
      reason: "Consultație cardiologie",
      status: "scheduled",
      notes: "Prima consultație",
      duration: 30,
      priority: "medium",
      createdAt: formatISO(subDays(now, 2)),
      updatedAt: formatISO(subDays(now, 1)),
    },
    {
      id: 2,
      PatientId: 2,
      DoctorId: 2,
      PatientName: "Mihai Constantinescu",
      DoctorName: "Dr. Ion Ionescu",
      date: format(addDays(now, 1), "yyyy-MM-dd"),
      time: "10:30",
      reason: "Control neurologic",
      status: "confirmed",
      notes: "Pacient cu istoric de migrene",
      duration: 45,
      priority: "high",
      createdAt: formatISO(subDays(now, 3)),
      updatedAt: formatISO(now),
    },
    {
      id: 3,
      PatientId: 3,
      DoctorId: 3,
      PatientName: "Diana Popescu",
      DoctorName: "Dr. Ana Vasilescu",
      date: format(addDays(now, 2), "yyyy-MM-dd"),
      time: "14:00",
      reason: "Vaccinare copil",
      status: "scheduled",
      duration: 15,
      priority: "low",
      createdAt: formatISO(subDays(now, 1)),
      updatedAt: formatISO(subDays(now, 1)),
    },
  ];
};

const AppointmentSchedule: React.FC<AppointmentScheduleProps> = ({
  title = "Programare Consultații",
  height = 700,
  defaultView = "week",
}) => {
  const theme = useTheme();
  dayjs.locale("ro");

  // State Management
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // View State
  const [viewMode, setViewMode] = useState<ViewMode>(defaultView);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"view" | "edit" | "add">("view");
  const [formData, setFormData] = useState<Partial<Appointment>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Filter State
  const [filters, setFilters] = useState<AppointmentFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  // ✅ Load Data Functions
  const loadAppointments = useCallback(
    async (filtersToApply?: AppointmentFilters) => {
      try {
        setLoading(true);
        setError(null);

        const currentFilters = filtersToApply || filters;

        console.log(
          "🌐 Loading appointments from API with filters:",
          currentFilters
        );

        const params = new URLSearchParams();

        if (currentFilters.doctorId)
          params.append("doctorId", currentFilters.doctorId.toString());
        if (currentFilters.patientId)
          params.append("patientId", currentFilters.patientId.toString());
        if (currentFilters.status)
          params.append("status", currentFilters.status);
        if (currentFilters.priority)
          params.append("priority", currentFilters.priority);
        if (currentFilters.dateFrom)
          params.append("dateFrom", formatISO(currentFilters.dateFrom));
        if (currentFilters.dateTo)
          params.append("dateTo", formatISO(currentFilters.dateTo));
        if (currentFilters.searchTerm)
          params.append("search", currentFilters.searchTerm);

        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-API-Key": process.env.REACT_APP_API_KEY,
          },
          timeout: 10000,
        };

        const response = await axios.get(
          `${API_BASE_URL}${API_ENDPOINTS.appointments}?${params.toString()}`,
          config
        );

        console.log("✅ Appointments loaded successfully:", response.data);

        let appointmentsData: Appointment[] = [];
        if (Array.isArray(response.data)) {
          appointmentsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          appointmentsData = response.data.data;
        }

        setAppointments(appointmentsData);
      } catch (err) {
        console.error("❌ Error loading appointments:", err);
        setError("Failed to load appointments");

        // Fallback to mock data in development
        if (process.env.NODE_ENV === "development") {
          console.log("🔄 Using mock appointments data");
          setAppointments(generateMockAppointments());
          setError("Using mock data - API unavailable");
        }
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  const loadDoctors = useCallback(async () => {
    try {
      console.log("👨‍⚕️ Loading doctors from API");

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      };

      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.doctors}`,
        config
      );
      setDoctors(response.data.data || response.data || []);
    } catch (err) {
      console.log("Using mock doctors data");
      setDoctors(generateMockDoctors());
    }
  }, []);

  const loadPatients = useCallback(async () => {
    try {
      console.log("👥 Loading patients from API");

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      };

      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.patients}`,
        config
      );
      setPatients(response.data.data || response.data || []);
    } catch (err) {
      console.log("Using mock patients data");
      setPatients(generateMockPatients());
    }
  }, []);

  // ✅ CRUD Operations
  const handleSaveAppointment = useCallback(
    async (isEdit: boolean) => {
      try {
        console.log("💾 Saving appointment:", { isEdit, formData });

        setLoading(true);
        setFormErrors({});

        // Validation
        const errors: Record<string, string> = {};

        if (!formData.PatientId)
          errors.PatientId = "Pacientul este obligatoriu";
        if (!formData.DoctorId) errors.DoctorId = "Doctorul este obligatoriu";
        if (!formData.date) errors.date = "Data este obligatorie";
        if (!formData.time) errors.time = "Ora este obligatorie";
        if (!formData.reason?.trim())
          errors.reason = "Motivul este obligatoriu";

        if (Object.keys(errors).length > 0) {
          setFormErrors(errors);
          console.log("❌ Validation errors:", errors);
          return;
        }

        const appointmentData = {
          ...formData,
          status: formData.status || "scheduled",
          priority: formData.priority || "medium",
          duration: formData.duration || 30,
        };

        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        };

        let response;

        if (isEdit && appointmentData.id) {
          console.log("✏️ Updating appointment:", appointmentData.id);
          response = await axios.put(
            `${API_BASE_URL}${API_ENDPOINTS.updateAppointment}/${appointmentData.id}`,
            appointmentData,
            config
          );
        } else {
          console.log("➕ Creating new appointment");
          response = await axios.post(
            `${API_BASE_URL}${API_ENDPOINTS.createAppointment}`,
            appointmentData,
            config
          );
        }

        console.log("✅ Appointment saved successfully:", response.data);

        await loadAppointments();
        handleDialogClose();
      } catch (err) {
        console.error("❌ Error saving appointment:", err);

        if (axios.isAxiosError(err) && err.response) {
          if (err.response.status === 409) {
            setError("Conflict: Time slot already booked");
          } else {
            setError(`Failed to save appointment: ${err.response.statusText}`);
          }
        } else {
          setError("Failed to save appointment");
        }
      } finally {
        setLoading(false);
      }
    },
    [formData, loadAppointments]
  );

  const handleDeleteAppointment = useCallback(
    async (appointment: Appointment) => {
      if (
        !window.confirm(
          `Sigur doriți să ștergeți programarea pentru ${appointment.PatientName}?`
        )
      ) {
        return;
      }

      try {
        console.log("🗑️ Deleting appointment:", appointment.id);
        setLoading(true);

        const config = {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        };

        await axios.delete(
          `${API_BASE_URL}${API_ENDPOINTS.deleteAppointment}/${appointment.id}`,
          config
        );

        console.log("✅ Appointment deleted successfully");
        await loadAppointments();
      } catch (err) {
        console.error("❌ Error deleting appointment:", err);
        setError("Failed to delete appointment");
      } finally {
        setLoading(false);
      }
    },
    [loadAppointments]
  );

  // ✅ Dialog Handlers
  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setSelectedAppointment(null);
    setFormData({});
    setFormErrors({});
  }, []);

  const handleView = useCallback((appointment: Appointment) => {
    console.log("👁️ Viewing appointment:", appointment.id);
    setSelectedAppointment(appointment);
    setFormData(appointment);
    setDialogMode("view");
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((appointment: Appointment) => {
    console.log("✏️ Editing appointment:", appointment.id);
    setSelectedAppointment(appointment);
    setFormData(appointment);
    setDialogMode("edit");
    setDialogOpen(true);
  }, []);

  // ✅ Helper functions to convert between date-fns and dayjs
  const dateToString = useCallback((date: Date | string | null): string => {
    if (!date) return "";
    if (typeof date === "string") return date;
    return format(date, "yyyy-MM-dd");
  }, []);

  const handleAdd = useCallback(() => {
    console.log("➕ Adding new appointment");
    setSelectedAppointment(null);
    setFormData({
      date: dateToString(currentDate), // ✅ Convert Date to string
      time: "09:00",
      status: "scheduled",
      priority: "medium",
      duration: 30,
    });
    setFormErrors({});
    setDialogMode("add");
    setDialogOpen(true);
  }, [currentDate, dateToString]);

  const timeToString = useCallback((time: Date | Dayjs | null): string => {
    if (!time) return "";
    if (dayjs.isDayjs(time)) {
      return time.format("HH:mm");
    }
    return format(time as Date, "HH:mm");
  }, []);

  const stringToDate = useCallback((dateStr: string): Date | null => {
    if (!dateStr) return null;
    try {
      return parseISO(dateStr);
    } catch {
      return null;
    }
  }, []);

  const stringToDayjs = useCallback((dateStr: string): Dayjs | null => {
    if (!dateStr) return null;
    return dayjs(dateStr);
  }, []);

  const stringToTime = useCallback((timeStr: string): Dayjs | null => {
    if (!timeStr) return null;
    // Create a dayjs object with today's date and the specified time
    return dayjs(`2000-01-01T${timeStr}:00`);
  }, []);

  // ✅ Enhanced Form Field Handler
  const handleFormFieldChange = useCallback(
    (field: keyof Appointment, value: any) => {
      console.log(`📝 Form field changed: ${field} =`, value);

      setFormData((prev) => {
        const newData = { ...prev };

        if (field === "date") {
          // Convert Dayjs to string
          newData[field] = dayjs.isDayjs(value)
            ? value.format("YYYY-MM-DD")
            : value;
        } else if (field === "time") {
          // Convert Dayjs to time string
          newData[field] = dayjs.isDayjs(value) ? value.format("HH:mm") : value;
        } else {
          newData[field] = value;
        }

        return newData;
      });

      if (formErrors[field]) {
        setFormErrors((prev) => ({ ...prev, [field]: "" }));
      }
    },
    [formErrors]
  );

  // ✅ Navigation Handlers
  const handleNavigate = useCallback(
    (direction: "prev" | "next") => {
      setCurrentDate((prev) => {
        switch (viewMode) {
          case "day":
            return direction === "next" ? addDays(prev, 1) : subDays(prev, 1);
          case "week":
            return direction === "next" ? addWeeks(prev, 1) : subWeeks(prev, 1);
          case "month":
            return direction === "next"
              ? addMonths(prev, 1)
              : subMonths(prev, 1);
          default:
            return prev;
        }
      });
    },
    [viewMode]
  );

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // ✅ Status Color Helper
  const getStatusColor = (
    status: string
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    switch (status) {
      case "scheduled":
        return "primary";
      case "confirmed":
        return "success";
      case "completed":
        return "info";
      case "canceled":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityColor = (
    priority: string
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "primary";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  // ✅ Filter Helpers
  const filteredAppointments = useMemo(() => {
    let filtered = [...appointments];

    // Apply date range filter based on view
    const startDate =
      viewMode === "month"
        ? startOfMonth(currentDate)
        : viewMode === "week"
        ? startOfWeek(currentDate, { weekStartsOn: 1 })
        : currentDate;

    const endDate =
      viewMode === "month"
        ? endOfMonth(currentDate)
        : viewMode === "week"
        ? endOfWeek(currentDate, { weekStartsOn: 1 })
        : currentDate;

    if (viewMode !== "list") {
      filtered = filtered.filter((appointment) => {
        const appointmentDate = parseISO(appointment.date);
        return appointmentDate >= startDate && appointmentDate <= endDate;
      });
    }

    return filtered;
  }, [appointments, currentDate, viewMode]);

  // ✅ Effects
  useEffect(() => {
    loadAppointments();
    loadDoctors();
    loadPatients();
  }, [loadAppointments, loadDoctors, loadPatients]);

  useEffect(() => {
    loadAppointments();
  }, [currentDate, viewMode, loadAppointments]);

  // ✅ Render Calendar Grid for Week/Month View
  const renderCalendarGrid = () => {
    if (viewMode === "list") {
      return (
        <Grid container spacing={2}>
          {filteredAppointments.map((appointment) => (
            <Grid item xs={12} key={appointment.id}>
              <Card
                elevation={2}
                sx={{
                  borderLeft: `4px solid ${
                    getStatusColor(appointment.status) === "default"
                      ? theme.palette.grey[400]
                      : theme.palette[
                          getStatusColor(appointment.status) as
                            | "primary"
                            | "secondary"
                            | "error"
                            | "info"
                            | "success"
                            | "warning"
                        ]?.main || theme.palette.primary.main
                  }`, // ✅ Fixed border color
                  "&:hover": { elevation: 4 },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" component="div">
                      {appointment.PatientName}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Chip
                        label={appointment.status}
                        color={getStatusColor(appointment.status)}
                        size="small"
                      />
                      <Chip
                        label={appointment.priority}
                        color={getPriorityColor(
                          appointment.priority || "medium"
                        )}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>

                  <Typography color="text.secondary" gutterBottom>
                    {appointment.DoctorName} • {appointment.date} •{" "}
                    {appointment.time}
                  </Typography>

                  <Typography variant="body2">{appointment.reason}</Typography>

                  {appointment.notes && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontStyle: "italic" }}
                    >
                      📝 {appointment.notes}
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    onClick={() => handleView(appointment)}
                    startIcon={<ViewIcon />}
                  >
                    Vezi
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleEdit(appointment)}
                    startIcon={<EditIcon />}
                  >
                    Editează
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDeleteAppointment(appointment)}
                    startIcon={<DeleteIcon />}
                  >
                    Șterge
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    // Week/Month calendar grid implementation
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {format(currentDate, "MMMM yyyy", { locale: ro })}
        </Typography>
        <Grid container spacing={1}>
          {filteredAppointments.map((appointment) => (
            <Grid item xs={12} sm={6} md={4} key={appointment.id}>
              <Card
                elevation={1}
                sx={{
                  cursor: "pointer",
                  "&:hover": { elevation: 3 },
                }}
                onClick={() => handleView(appointment)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle2" noWrap>
                    {appointment.time} - {appointment.PatientName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {appointment.reason}
                  </Typography>
                  <Chip
                    label={appointment.status}
                    color={getStatusColor(appointment.status)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ro">
      <Box sx={{ width: "100%", height }}>
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {title}
          </Typography>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Programare nouă
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* Navigation */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={() => handleNavigate("prev")} size="small">
                <PrevIcon />
              </IconButton>

              <Button
                variant="outlined"
                onClick={handleToday}
                size="small"
                startIcon={<TodayIcon />}
              >
                Astăzi
              </Button>

              <IconButton onClick={() => handleNavigate("next")} size="small">
                <NextIcon />
              </IconButton>

              <Typography variant="h6" sx={{ ml: 2, minWidth: 200 }}>
                {viewMode === "month" &&
                  format(currentDate, "MMMM yyyy", { locale: ro })}
                {viewMode === "week" &&
                  `${format(
                    startOfWeek(currentDate, { weekStartsOn: 1 }),
                    "dd MMM",
                    { locale: ro }
                  )} - ${format(
                    endOfWeek(currentDate, { weekStartsOn: 1 }),
                    "dd MMM yyyy",
                    { locale: ro }
                  )}`}
                {viewMode === "day" &&
                  format(currentDate, "dd MMMM yyyy", { locale: ro })}
                {viewMode === "list" && "Toate programările"}
              </Typography>
            </Box>

            {/* View Mode Buttons */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant={viewMode === "day" ? "contained" : "outlined"}
                size="small"
                onClick={() => setViewMode("day")}
                startIcon={<CalendarToday />}
              >
                Zi
              </Button>
              <Button
                variant={viewMode === "week" ? "contained" : "outlined"}
                size="small"
                onClick={() => setViewMode("week")}
                startIcon={<CalendarViewWeek />}
              >
                Săptămână
              </Button>
              <Button
                variant={viewMode === "month" ? "contained" : "outlined"}
                size="small"
                onClick={() => setViewMode("month")}
                startIcon={<CalendarViewMonth />}
              >
                Lună
              </Button>
              <Button
                variant={viewMode === "list" ? "contained" : "outlined"}
                size="small"
                onClick={() => setViewMode("list")}
                startIcon={<EventIcon />}
              >
                Listă
              </Button>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <IconButton
                onClick={() => loadAppointments()}
                disabled={loading}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
        </Paper>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Calendar Content */}
        <Paper
          elevation={2}
          sx={{ borderRadius: 3, overflow: "hidden", minHeight: 500 }}
        >
          {renderCalendarGrid()}
        </Paper>

        {/* Appointment Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              fontWeight: 600,
            }}
          >
            {dialogMode === "view" && "Detalii Programare"}
            {dialogMode === "edit" && "Editare Programare"}
            {dialogMode === "add" && "Programare Nouă"}
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  select
                  label="Pacient *"
                  value={formData.PatientId || ""}
                  onChange={(e) =>
                    handleFormFieldChange("PatientId", Number(e.target.value))
                  }
                  disabled={dialogMode === "view"}
                  fullWidth
                  error={!!formErrors.PatientId}
                  helperText={formErrors.PatientId}
                >
                  {patients.map((patient) => (
                    <MenuItem key={patient.id} value={patient.id}>
                      {patient.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Doctor *"
                  value={formData.DoctorId || ""}
                  onChange={(e) =>
                    handleFormFieldChange("DoctorId", Number(e.target.value))
                  }
                  disabled={dialogMode === "view"}
                  fullWidth
                  error={!!formErrors.DoctorId}
                  helperText={formErrors.DoctorId}
                >
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.id}>
                      {doctor.name} - {doctor.specialization}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                {/* ✅ Fixed DatePicker */}
                <DatePicker
                  label="Data *"
                  value={stringToDayjs(formData.date || "")} // ✅ Convert string to Dayjs
                  onChange={(date) => handleFormFieldChange("date", date)} // ✅ Pass Dayjs directly
                  disabled={dialogMode === "view"}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.date,
                      helperText: formErrors.date,
                    },
                  }}
                />

                {/* ✅ Fixed TimePicker */}
                <TimePicker
                  label="Ora *"
                  value={stringToTime(formData.time || "")} // ✅ Convert time string to Dayjs
                  onChange={(time) => handleFormFieldChange("time", time)} // ✅ Pass Dayjs directly
                  disabled={dialogMode === "view"}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.time,
                      helperText: formErrors.time,
                    },
                  }}
                />
              </Box>

              <TextField
                label="Motivul consultației *"
                value={formData.reason || ""}
                onChange={(e) =>
                  handleFormFieldChange("reason", e.target.value)
                }
                disabled={dialogMode === "view"}
                fullWidth
                multiline
                rows={2}
                error={!!formErrors.reason}
                helperText={formErrors.reason}
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  select
                  label="Status"
                  value={formData.status || "scheduled"}
                  onChange={(e) =>
                    handleFormFieldChange("status", e.target.value)
                  }
                  disabled={dialogMode === "view"}
                  fullWidth
                >
                  <MenuItem value="scheduled">Programat</MenuItem>
                  <MenuItem value="confirmed">Confirmat</MenuItem>
                  <MenuItem value="completed">Finalizat</MenuItem>
                  <MenuItem value="canceled">Anulat</MenuItem>
                </TextField>

                <TextField
                  select
                  label="Prioritate"
                  value={formData.priority || "medium"}
                  onChange={(e) =>
                    handleFormFieldChange("priority", e.target.value)
                  }
                  disabled={dialogMode === "view"}
                  fullWidth
                >
                  <MenuItem value="low">Scăzută</MenuItem>
                  <MenuItem value="medium">Medie</MenuItem>
                  <MenuItem value="high">Ridicată</MenuItem>
                  <MenuItem value="urgent">Urgentă</MenuItem>
                </TextField>

                <TextField
                  label="Durata (min)"
                  type="number"
                  value={formData.duration || 30}
                  onChange={(e) =>
                    handleFormFieldChange("duration", Number(e.target.value))
                  }
                  disabled={dialogMode === "view"}
                  inputProps={{ min: 15, max: 180, step: 15 }}
                />
              </Box>

              <TextField
                label="Observații"
                value={formData.notes || ""}
                onChange={(e) => handleFormFieldChange("notes", e.target.value)}
                disabled={dialogMode === "view"}
                fullWidth
                multiline
                rows={3}
                placeholder="Observații suplimentare..."
              />
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{ p: 3, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              disabled={loading}
            >
              {dialogMode === "view" ? "Închide" : "Anulează"}
            </Button>

            {dialogMode !== "view" && (
              <Button
                variant="contained"
                onClick={() => handleSaveAppointment(dialogMode === "edit")}
                disabled={loading}
                sx={{ minWidth: 140, fontWeight: 600 }}
              >
                {loading
                  ? "Se salvează..."
                  : dialogMode === "edit"
                  ? "Salvează Modificările"
                  : "Creează Programarea"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          sx={{ position: "fixed", bottom: 24, right: 24 }}
          onClick={handleAdd}
        >
          <AddIcon />
        </Fab>
      </Box>
    </LocalizationProvider>
  );
};

export default AppointmentSchedule;
